import {
  addDoc,
  collection,
  doc,
  getDocs,
  getFirestore,
  query,
  serverTimestamp,
  updateDoc,
  where,
  runTransaction,
} from "firebase/firestore";
import firebaseApp from "../../firebase/credenciales";
import { registrarAsientoTesoreria } from "../contabilidad/asientosService";
import { registrarDocumentoComercial } from "../contabilidad/documentosComercialesService";

const db = getFirestore(firebaseApp);
const TESORERIA_COLLECTION = "gte_movimientos";

const normalizarTexto = (valor) => (valor == null ? "" : String(valor).trim());
const normalizarNumero = (valor) => {
  const parsed = Number(valor);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(2)) : 0;
};

export const registrarMovimientoTesoreria = async ({ rucEmpresa, movimiento, usuarioRegistro }) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) {
    throw new Error("No se pudo registrar el movimiento: empresa no válida.");
  }

  const tipo = normalizarTexto(movimiento?.tipo || "").toUpperCase();
  if (!["PAGO", "COBRO"].includes(tipo)) {
    throw new Error("Tipo de movimiento inválido. Use PAGO o COBRO.");
  }

  const monto = normalizarNumero(movimiento?.monto);
  if (monto <= 0) {
    throw new Error("El monto debe ser mayor a cero.");
  }

  const payload = {
    ruc_empresa: ruc,
    tipo,
    fecha: normalizarTexto(movimiento?.fecha),
    descripcion: normalizarTexto(movimiento?.descripcion),
    tercero: normalizarTexto(movimiento?.tercero),
    categoria: normalizarTexto(movimiento?.categoria),
    medio_pago: normalizarTexto(movimiento?.medioPago || "TRANSFERENCIA"),
    referencia: normalizarTexto(movimiento?.referencia),
    moneda: normalizarTexto(movimiento?.moneda || "PEN"),
    monto,
    estado: "REGISTRADO",
    documentos_relacionados: Array.isArray(movimiento?.documentosRelacionados) ? movimiento.documentosRelacionados : [],
    asiento_id: "",
    estado_contable: "PENDIENTE",
    usuario_registro: normalizarTexto(usuarioRegistro),
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const docRef = doc(collection(db, TESORERIA_COLLECTION));
  const refId = docRef.id;

  const documentosRelacionados = Array.isArray(payload.documentos_relacionados)
    ? [...payload.documentos_relacionados]
    : [];

  const documentoRelacionado = movimiento?.documentoRelacionado || null;
  if (documentoRelacionado && normalizarTexto(documentoRelacionado.tipo)) {
    documentosRelacionados.push({
      tipo: normalizarTexto(documentoRelacionado.tipo).toUpperCase(),
      serie: normalizarTexto(documentoRelacionado.serie),
      numero: normalizarTexto(documentoRelacionado.numero),
      referencia: normalizarTexto(documentoRelacionado.id || ""),
    });
  }

  if (normalizarTexto(payload.medio_pago).toUpperCase() === "DEPOSITO") {
    const docDepositoId = await registrarDocumentoComercial({
      rucEmpresa: ruc,
      tipoDocumento: "DEPOSITO_BANCARIO",
      serie: "DB",
      numero: normalizarTexto(payload.referencia || refId),
      fecha: payload.fecha,
      monto,
      moneda: payload.moneda,
      moduloOrigen: "GESTION_TESORERIA",
      entidadId: refId,
      entidadTipo: "MOVIMIENTO_TESORERIA",
      relacionadoCon: documentosRelacionados,
      metadata: {
        tipo_movimiento: payload.tipo,
        tercero: payload.tercero,
      },
    });
    documentosRelacionados.push({ id: docDepositoId, tipo: "DEPOSITO_BANCARIO" });
  }

  payload.documentos_relacionados = documentosRelacionados;

  try {
    await runTransaction(db, async (transaction) => {
      const relacionPlanilla = documentosRelacionados.find(
        (docRel) => normalizarTexto(docRel.tipo).toUpperCase() === "PLANILLA"
      );

      let updatePlanillaPayload = null;
      let planillaRefToUpdate = null;

      if (relacionPlanilla) {
        const planillaId = normalizarTexto(relacionPlanilla.referencia || relacionPlanilla.id);
        if (!planillaId) throw new Error("ID de planilla inválido para el pago.");

        const planillaRef = doc(db, "pl_planillas", planillaId);
        const planillaSnap = await transaction.get(planillaRef);
        if (!planillaSnap.exists()) {
          throw new Error(`La planilla con ID ${planillaId} no existe.`);
        }

        const planillaData = planillaSnap.data();
        if (normalizarTexto(planillaData.estado).toUpperCase() !== "APROBADA") {
          throw new Error("No se puede pagar una planilla que no está en estado APROBADA.");
        }

        const provSeatId = `${planillaId}_PROVISION`;
        const provRef = doc(db, "gco_asientos", provSeatId);
        const provSnap = await transaction.get(provRef);
        if (!provSnap.exists()) {
          throw new Error("No se puede pagar la planilla porque no cuenta con provisión contable en gco_asientos.");
        }

        const totalObligacion = Number(planillaData.total_neto || 0);
        const montoPagadoAnterior = Number(planillaData.monto_pagado || 0);
        const saldoPendienteActual = Number((totalObligacion - montoPagadoAnterior).toFixed(2));

        if (monto > saldoPendienteActual) {
          throw new Error(`El monto del pago (S/ ${monto}) supera el saldo pendiente de la planilla (S/ ${saldoPendienteActual}).`);
        }

        const nuevoMontoPagado = Number((montoPagadoAnterior + monto).toFixed(2));
        planillaRefToUpdate = planillaRef;
        updatePlanillaPayload = {
          monto_pagado: nuevoMontoPagado,
          saldo_pendiente: Number((totalObligacion - nuevoMontoPagado).toFixed(2)),
          estado_pago: nuevoMontoPagado >= totalObligacion ? "PAGADO" : "PAGO_PARCIAL",
        };
      }

      const asientoId = `${normalizarTexto(refId)}_TESORERIA_${tipo}`;

      payload.asiento_id = asientoId;
      payload.estado_contable = "GENERADO";

      // 1. All READS occur inside registrarAsientoTesoreria (period check, idempotency check, CUO check) BEFORE any writes!
      await registrarAsientoTesoreria({
        rucEmpresa: ruc,
        movimientoId: refId,
        tipo,
        fecha: payload.fecha,
        tercero: payload.tercero,
        descripcion: payload.descripcion,
        categoria: payload.categoria,
        medioPago: payload.medio_pago,
        monto,
        moneda: payload.moneda,
        tipoCambio: 1,
      }, transaction);

      // 2. All WRITES occur after all reads have completed
      transaction.set(docRef, payload);

      if (planillaRefToUpdate && updatePlanillaPayload) {
        transaction.update(planillaRefToUpdate, updatePlanillaPayload);
      }
    });
  } catch (error) {
    throw new Error(`Tesoreria registration aborted: ${error.message}`);
  }

  return refId;
};

export const obtenerMovimientosTesoreriaPorEmpresa = async (rucEmpresa) => {
  const ruc = normalizarTexto(rucEmpresa);
  if (!ruc) return [];

  const q = query(collection(db, TESORERIA_COLLECTION), where("ruc_empresa", "==", ruc));
  const snap = await getDocs(q);

  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .sort((a, b) => String(b.fecha || "").localeCompare(String(a.fecha || "")));
};
